import { useCallback, useEffect, useRef } from 'react';
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, authReady } from '../firebase';

/**
 * Записывает посещение курса чтения учеником: одно посещение — один документ
 * в readSessions. В нём общее время, время по неделям курса и клики вида
 * «Неделя 3–4 › Text 3 · The Bug in the Mug › строка рассказа».
 *
 * Время идёт только пока вкладка на экране: курс, оставленный открытым в фоне
 * на ночь, не должен выглядеть восемью часами занятий.
 *
 * Клики слушаем снаружи, из страницы сайта: курс в кадре того же источника,
 * поэтому его документ доступен, и сам курс ради статистики не меняется.
 *
 * Админ не записывается — он заходит в курс проверить, а не учиться.
 */
const TICK_S = 15;   // как часто прибавляем время
const SAVE_S = 60;   // как часто сохраняем, чтобы закрытая вкладка теряла не больше минуты

const clean = (t) => (t || '').replace(/\s+/g, ' ').replace(/^[^\p{L}\p{N}]+/u, '').trim().slice(0, 40);

/** Неделя, которая сейчас открыта в курсе. */
const weekOf = (d) => clean(d.querySelector('.seg.on span')?.textContent) || 'Сегодня';

/** Человеческое имя того, по чему кликнули. */
function describe(el, d) {
  const seg = el.closest('.seg');
  if (seg) return null;                                  // переход между неделями — это время по неделям, не клик
  const card = el.closest('.card');
  const block = card
    ? clean(card.querySelector('h2.sec')?.textContent) || (card.querySelector('h1') ? 'Вступление' : 'Карточка')
    : clean(el.closest('details')?.querySelector('summary')?.textContent) || 'Страница';
  let action;
  if (el.matches('.ln')) action = 'строка рассказа';
  else if (el.matches('.qq')) action = 'вопрос';
  else if (el.matches('.bw')) action = 'слово в колоде';
  else if (el.matches('summary')) action = 'раскрыл раздел';
  else if (el.matches('[data-say]') && !el.matches('.btn')) action = 'слово';
  else action = clean(el.textContent) || el.getAttribute('title') || 'кнопка';
  return `${weekOf(d)} › ${block} › ${action}`;
}

export default function useReadSession() {
  const attachRef = useRef(() => {});

  useEffect(() => {
    const studentId = localStorage.getItem('studentId');
    if (localStorage.getItem('isAdmin') === 'true' || !studentId) return undefined;

    const ref = doc(collection(db, 'readSessions'));
    let seconds = 0;
    let sinceSave = 0;
    let lastTick = Date.now();
    let frameDoc = null;
    const clicks = {};
    const weeks = {};
    let dirty = false;

    // Страница теперь рисуется до анонимного входа, а запись без него не пройдёт
    const save = () => {
      sinceSave = 0;
      dirty = false;
      authReady.then(() => setDoc(ref, { seconds, weeks, clicks, lastSeenAt: serverTimestamp() }, { merge: true })).catch(() => {});
    };

    authReady.then(() => setDoc(ref, {
      studentId,
      startedAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
      seconds: 0,
      device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'телефон' : 'компьютер',
    })).catch(() => {});

    const tick = () => {
      const now = Date.now();
      // Таймеры в фоне засыпают — прибавляем реально прошедшее, но не больше шага
      if (document.visibilityState === 'visible') {
        const step = Math.min(TICK_S, Math.round((now - lastTick) / 1000));
        seconds += step;
        sinceSave += step;
        if (frameDoc) {
          const w = weekOf(frameDoc);
          weeks[w] = (weeks[w] || 0) + step;
        }
      }
      lastTick = now;
      if (sinceSave >= SAVE_S || (dirty && sinceSave >= TICK_S)) save();
    };
    const timer = setInterval(tick, TICK_S * 1000);

    const onClick = (e) => {
      const el = e.target.closest?.('button, a, summary, [data-say], .bw');
      if (!el) return;
      const key = describe(el, frameDoc);
      if (!key) return;
      clicks[key] = (clicks[key] || 0) + 1;
      dirty = true;
    };

    attachRef.current = (iframe) => {
      try {
        const d = iframe?.contentDocument;
        if (!d || d === frameDoc) return;
        frameDoc = d;
        d.addEventListener('click', onClick, true);
      } catch { /* кадр другого источника — просто не считаем клики */ }
    };

    const onHide = () => {
      if (document.visibilityState === 'hidden') { tick(); save(); }
      else lastTick = Date.now();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', save);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', save);
      try { frameDoc?.removeEventListener('click', onClick, true); } catch { /* кадр уже выгружен */ }
      attachRef.current = () => {};
      tick();
      save();
    };
  }, []);

  // Вызывается из onLoad кадра с курсом
  return useCallback((iframe) => attachRef.current(iframe), []);
}
